import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/safety-hygiene.svg";
import WhatsAppButton from "../components/WhatsappButton";

const SafetyHygiene: React.FC = () => {
  const { t } = useLanguage();

  const safetyGuidelines = t("safetyHygiene.safetyGuidelines");
  const hygieneProtocols = t("safetyHygiene.hygieneProtocols");
  const educationalSpaces = t("safetyHygiene.educationalSpaces");
  const atmosphereElements = t("safetyHygiene.atmosphereElements");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          {/* Decorative SVGs */}
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[280px] w-full"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[200px] sm:max-w-[220px] md:max-w-[240px] lg:max-w-[290px] w-full"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("safetyHygiene.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-neutral-800 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("safetyHygiene.hero.subtitle")}
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-6 leading-tight">
                {t("safetyHygiene.intro.title")}
              </h2>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                    {t("safetyHygiene.intro.text1")}
                  </p>
                  <p className="text-lg text-neutral-800 leading-relaxed">
                    {t("safetyHygiene.intro.text2")}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-2xl p-6">
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
                    {t("safetyHygiene.intro.commitmentTitle")}
                  </h3>
                  <div className="space-y-3">
                    {t("safetyHygiene.intro.commitments").map(
                      (point: string, idx: number) => (
                        <div key={idx} className="space-x-3">
                          <span className="text-neutral-800">• {point}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Physical Safety Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("safetyHygiene.physicalSafety.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("safetyHygiene.physicalSafety.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {safetyGuidelines.map((guideline: any, index: number) => (
                <div key={index} className="group">
                  <div
                    className={`${guideline.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                  >
                    <div className="text-4xl mb-4">{guideline.icon}</div>
                    <h3 className="text-lg font-semibold mb-3">
                      {guideline.title}
                    </h3>
                    <p className="text-md opacity-90">
                      {guideline.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-semibold text-neutral-900 mb-6 text-center">
                {t("safetyHygiene.physicalSafety.complianceTitle")}
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                    {t("safetyHygiene.physicalSafety.inspectionsTitle")}
                  </h4>
                  <ul className="space-y-2 text-neutral-800">
                    {t("safetyHygiene.physicalSafety.inspections").map(
                      (item: string, idx: number) => (
                        <li key={idx}>• {item}</li>
                      )
                    )}
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                    {t("safetyHygiene.physicalSafety.standardsTitle")}
                  </h4>
                  <ul className="space-y-2 text-neutral-800">
                    {t("safetyHygiene.physicalSafety.standards").map(
                      (item: string, idx: number) => (
                        <li key={idx}>• {item}</li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hygiene Protocols */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("safetyHygiene.hygiene.title")}
            </h2>
            <p className="text-xl md:text-2xl text-neutral-800 max-w-3xl mx-auto">
              {t("safetyHygiene.hygiene.subtitle")}
            </p>
          </div>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
            {hygieneProtocols.map((protocol: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div
                    className={`w-16 h-16 rounded-full ${protocol.color} flex items-center justify-center text-2xl text-white`}
                  >
                    {protocol.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-neutral-900">
                      {protocol.title}
                    </h3>
                    <p className="text-neutral-800">{protocol.description}</p>
                  </div>
                </div>
                <ul className="space-y-3 w-fit mx-auto my-auto">
                  {protocol.details.map((detail: string, idx: number) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-[#492A19]" />
                      <span className="text-neutral-800">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Educational Spaces */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("safetyHygiene.spaces.title")}
            </h2>
            <p className="text-xl md:text-2xl text-neutral-800 max-w-3xl mx-auto">
              {t("safetyHygiene.spaces.subtitle")}
            </p>
          </div>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
            {educationalSpaces.map((space: any, index: number) => (
              <div key={index} className="group">
                <div
                  className={`${space.color} rounded-3xl p-8 text-white h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="text-4xl">{space.icon}</div>
                    <div>
                      <h3 className="text-2xl font-semibold">{space.title}</h3>
                      <p className="opacity-90">{space.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {space.features.map((feature: string, idx: number) => (
                      <li
                        key={idx}
                        className="flex items-center space-x-3 opacity-90"
                      >
                        <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Atmosphere */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("safetyHygiene.atmosphere.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("safetyHygiene.atmosphere.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {atmosphereElements.map((element: any, index: number) => (
                <div key={index} className="group">
                  <div
                    className={`${element.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                  >
                    <div className="text-4xl mb-4">{element.icon}</div>
                    <h3 className="text-lg font-semibold mb-3">
                      {element.title}
                    </h3>
                    <p className="text-md opacity-90">{element.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                    {t("safetyHygiene.atmosphere.calmTitle")}
                  </h3>
                  <ul className="space-y-3 text-neutral-800">
                    {t("safetyHygiene.atmosphere.calmPoints").map(
                      (point: string, idx: number) => (
                        <li key={idx}>• {point}</li>
                      )
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                    {t("safetyHygiene.atmosphere.orderTitle")}
                  </h3>
                  <ul className="space-y-3 text-neutral-800">
                    {t("safetyHygiene.atmosphere.orderPoints").map(
                      (point: string, idx: number) => (
                        <li key={idx}>• {point}</li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        {/* <div className="container mx-auto px-4 py-16">
          <div className="text-center font-child">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-neutral-900 mb-4">
                {t("safetyHygiene.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("safetyHygiene.cta.subtitle")}
              </p>
              <button
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("safetyHygiene.cta.button")}
              </button>
            </div>
          </div>
        </div> */}
      </div>
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default SafetyHygiene;
